<template>
  <el-dialog :title="dialogTitle" :visible.sync="localVisible" center destroy-on-close width="75%">
    <v-client-table class="hiaTable" :data="notifyData" :columns="tableColumns" :options="notifyOptions" element-loading-spinner="atom-audit-loader">
      <template slot="name" slot-scope="props">
        {{ props.row.name ? props.row.name : '[not specified]' }}
      </template>
      <template slot="recordCount" slot-scope="props">
        {{ props.row.recordCount }}
      </template>
      <template slot="accountStatus" slot-scope="props">
        <span class="account-status">
          <i v-if="props.row.accountStatus === 'Active'" class="el-icon-circle-check account-status-icon account-status-icon--active"></i>
          <i v-else-if="props.row.accountStatus === 'No Link'" class="el-icon-warning-outline account-status-icon account-status-icon--no-link"></i>
          {{ props.row.accountStatus }}
        </span>
      </template>
    </v-client-table>

    <small>{{ notifyHint }}</small>
    <span slot="footer">
      <el-button-group>
        <el-button :disabled="confirmInProgress" :loading="confirmInProgress" @click="confirmNotify()" size="mini" plain round>Confirm</el-button>
        <el-button @click="localVisible = false" size="mini" plain round>Cancel</el-button>
      </el-button-group>
    </span>
  </el-dialog>
</template>

<script>
  import { isCoderNotifySelectable, isOriginalAuditorNotifySelectable } from './rebuttalTrackingSelect'

  export default {
    name: 'RebuttalNotifyDialog',
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      selectedRows: {
        type: Array,
        default: () => []
      },
      sender: {
        type: String,
        default: ''
      },
      myRecordsUrl: {
        type: String,
        default: ''
      },
      projectId: {
        type: Number,
        default: null
      },
      recordType: {
        type: String,
        default: ''
      },
      notifyTarget: {
        type: String,
        default: 'CODER',
        validator: value => ['CODER', 'ORIGINAL_AUDITOR'].includes(value)
      }
    },
    data() {
      return {
        confirmInProgress: false,
        notifyOptions: {
          filterByColumn: true,
          orderBy: {
            column: 'name',
            ascending: true
          },
          headings: {
            name: 'Name',
            email: 'Email',
            accountStatus: 'Account Status',
            recordCount: 'Record Count'
          },
          perPage: 25,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
        }
      }
    },
    computed: {
      localVisible: {
        get() {
          return this.visible
        },
        set(value) {
          this.$emit('update:visible', value)
        }
      },
      dialogTitle() {
        if (this.selectedRows.length > 0) {
          return this.notifyTarget === 'ORIGINAL_AUDITOR' ? 'Notify original auditors' : 'Notify coders'
        }
        return 'No Records Selected'
      },
      notifyHint() {
        if (this.notifyTarget === 'ORIGINAL_AUDITOR') {
          return 'Notifications can only be sent to original auditors with Active account status'
        }
        return 'Notifications can only be sent to coders with Active account status'
      },
      tableColumns() {
        return ['name', 'email', 'accountStatus', 'recordCount']
      },
      notifyData() {
        if (!this.selectedRows.length) {
          return []
        }

        return this.selectedRows.map((row) => ({
          name: this.notifyTarget === 'ORIGINAL_AUDITOR' ? row.originalAuditor : row.coder,
          email: this.notifyTarget === 'ORIGINAL_AUDITOR' ? row.originalAuditorEmail : row.coderEmail,
          accountStatus: row.accountStatus,
          recordCount: this.getRecordCount(row)
        }))
      }
    },
    watch: {
      visible(value) {
        if (value) {
          this.confirmInProgress = false
        }
      }
    },
    methods: {
      getRecordCount(row) {
        if (row.totalRecords != null && row.totalRecords !== '') {
          return row.totalRecords
        }
        return this.countAccountNumbers(row.accountNumbers)
      },
      countAccountNumbers(accountNumbers) {
        if (!accountNumbers) {
          return 0
        }
        if (Array.isArray(accountNumbers)) {
          return accountNumbers.length
        }
        return accountNumbers
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean).length
      },
      confirmNotify() {
        if (this.confirmInProgress) {
          return
        }
        this.confirmInProgress = true

        const payload = []

        for (let i = 0; i < this.selectedRows.length; i++) {
          const row = this.selectedRows[i]
          const isSelectable = this.notifyTarget === 'ORIGINAL_AUDITOR' ? isOriginalAuditorNotifySelectable(row) : isCoderNotifySelectable(row)

          if (isSelectable) {
            payload.push({
              coder: this.notifyTarget === 'ORIGINAL_AUDITOR' ? row.originalAuditor : row.coder,
              auditor: this.sender,
              coderEmail: this.notifyTarget === 'ORIGINAL_AUDITOR' ? row.originalAuditorEmail : row.coderEmail,
              recordCount: this.getRecordCount(row),
              myRecordsUrl: this.myRecordsUrl,
              projectId: Number(this.projectId),
              coderId: this.notifyTarget === 'ORIGINAL_AUDITOR' ? null : row.coderId,
              originalAuditorId: this.notifyTarget === 'ORIGINAL_AUDITOR' ? row.originalAuditorId : null,
              recordType: this.recordType,
              recordIds: row.accountNumbers
            })
          }
        }

        this.$emit('confirm', payload)
      }
    }
  }
</script>

<style scoped>
  .account-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .account-status-icon {
    font-size: 18px;
  }

  .account-status-icon--active {
    color: #67c23a;
  }

  .account-status-icon--no-link {
    color: #e6a23c;
  }
</style>
